import React, { useState, useEffect } from 'react';
import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query';
import { toastActions } from '../../../../components/uielements/toast/toast';
import { IntlMessages } from '../../../../helper/langHelper';
import AdminModal from '../../../../components/uielements/modal/adminModal';

import {
  FormWrapper,
  FormSection,
  FieldGroup,
  Label,
  ErrorText,
  ButtonContainer,
  PrimaryButton,
} from '../../users/modals/UserPromoteModal.styles';
import { createClusterFlow, updateClusterFlow } from '../../../../services/admin/clustersFlow';
import {
  getPrecedures,
  getPrecinctOfAddress,
  getZoneList,
} from '../../../../services/procedureService';
import { getClusters } from '../../../../services/admin/clusters';

const ClusterFlowCreateAndUpdateModal = ({
  onClose,
  isEdit = false,
  flowId = null,
  initialData = null,
}) => {
  const queryClient = useQueryClient();

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [regionId, setRegionId] = useState('');
  const [precinctId, setPrecinctId] = useState('');
  const [procedureId, setProcedureId] = useState('');
  const [selectedClusters, setSelectedClusters] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [apiError, setApiError] = useState('');

  // Dropdown data states
  const [proceduresData, setProceduresData] = useState([]);
  const [regionsData, setRegionsData] = useState([]);
  const [precinctsList, setPrecinctsList] = useState([]);
  const [clustersData, setClustersData] = useState([]);

  // React Query - Fetch Procedures
  const { data: proceduresQueryData, isLoading: proceduresLoading } = useQuery({
    queryKey: ['procedures', 0, 1000],
    queryFn: () => getPrecedures(0, 1000),
    enabled: true,
    staleTime: 5 * 60 * 1000, // 5 mins (data won't refetch)
    gcTime: 5 * 60 * 1000,
  });

  // React Query - Fetch Regions/Zones
  const { data: zonesQueryData, isLoading: zonesLoading } = useQuery({
    queryKey: ['zones'],
    queryFn: () => getZoneList(),
    enabled: true,
    staleTime: 5 * 60 * 1000, // 5 mins (data won't refetch)
    gcTime: 5 * 60 * 1000,
  });

  // React Query - Fetch Precincts based on selected region
  const { data: precinctQueryData, isLoading: precinctLoading } = useQuery({
    queryKey: ['precincts', regionId],
    queryFn: () => getPrecinctOfAddress({ regionId }),
    enabled: !!regionId,
  });

  // React Query - Fetch Clusters
  const { data: clustersQueryData, isLoading: clustersLoading } = useQuery({
    queryKey: ['clusters', 0, 1000],
    queryFn: () => getClusters(0, 1000),
    enabled: true,
  });

  // Populate dropdown data when queries succeed
  useEffect(() => {
    if (proceduresQueryData) {
      setProceduresData(proceduresQueryData);
    }
  }, [proceduresQueryData]);

  useEffect(() => {
    if (zonesQueryData) {
      setRegionsData(zonesQueryData);
    }
  }, [zonesQueryData]);

  useEffect(() => {
    if (precinctQueryData) {
      setPrecinctsList(precinctQueryData);
    } else {
      setPrecinctsList([]);
      setPrecinctId('');
    }
  }, [precinctQueryData]);

  useEffect(() => {
    if (clustersQueryData?.data) {
      setClustersData(clustersQueryData.data);
    }
  }, [clustersQueryData]);

  // Initialize with edit data
  // Normalize clusters to always be IDs only, whether they come as objects or strings
  useEffect(() => {
    if (isEdit && initialData) {
      setName(initialData.name || '');
      setDescription(initialData.description || '');

      // Normalize clusters: extract _id from objects, keep strings as-is
      const normalizedClusters = (initialData.clusterArray || []).map((cluster) => {
        if (typeof cluster === 'object' && cluster !== null && cluster._id) {
          return cluster._id;
        }
        return cluster; // Already a string ID
      });

      setSelectedClusters(normalizedClusters);
    }
  }, [isEdit, initialData]);

  // Create Mutation
  const createMutation = useMutation({
    mutationFn: (payload) => createClusterFlow(payload),
    onSuccess: () => {
      toastActions.success(
        <IntlMessages
          id="toast.clusterFlow.add.success"
          defaultMessage="Cluster Flow created successfully."
        />
      );
      queryClient.invalidateQueries({ queryKey: ['adminClustersFlow'] });
      handleClose();
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to create cluster flow. Please try again.';

      toastActions.error(
        <IntlMessages
          id="toast.clusterFlow.add.error"
          defaultMessage="Failed to create cluster flow."
        />
      );
      setApiError(errorMessage);
    },
  });

  // Update Mutation
  const updateMutation = useMutation({
    mutationFn: (payload) => updateClusterFlow(payload),
    onSuccess: () => {
      toastActions.success(
        <IntlMessages
          id="toast.clusterFlow.update.success"
          defaultMessage="Cluster Flow updated successfully."
        />
      );
      queryClient.invalidateQueries({ queryKey: ['adminClustersFlow'] });
      handleClose();
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to update cluster flow. Please try again.';

      toastActions.error(
        <IntlMessages
          id="toast.clusterFlow.update.error"
          defaultMessage="Failed to update cluster flow."
        />
      );
      setApiError(errorMessage);
    },
  });

  const isLoading =
    createMutation.isPending ||
    updateMutation.isPending ||
    proceduresLoading ||
    zonesLoading ||
    precinctLoading ||
    clustersLoading;

  // Validation
  const validateForm = () => {
    const errors = {};

    if (!name.trim()) {
      errors.name = 'Name is required.';
    }

    if (!description.trim()) {
      errors.description = 'Description is required.';
    }

    // Only validate these fields in create mode
    if (!isEdit) {
      if (!regionId) {
        errors.regionId = 'Region is required.';
      }

      if (!precinctId) {
        errors.precinctId = 'Precinct is required.';
      }

      if (!procedureId) {
        errors.procedureId = 'Development Type (Procedure) is required.';
      }
    }

    if (!selectedClusters || selectedClusters.length === 0) {
      errors.clusterArray = 'At least one cluster must be selected.';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle Submit
  const handleSubmit = (e) => {
    e.preventDefault();
    setApiError('');

    if (!validateForm()) {
      return;
    }

    if (isEdit) {
      // Edit mode - only send name, description, and clusterArray
      const payload = {
        flowId,
        name: name.trim(),
        description: description.trim(),
        clusterArray: selectedClusters,
      };

      updateMutation.mutate(payload);
    } else {
      // Create mode - send all fields
      const payload = {
        name: name.trim(),
        description: description.trim(),
        regionId,
        precinctId,
        procedureId,
        clusterArray: selectedClusters,
      };

      createMutation.mutate(payload);
    }
  };

  // Handle Close
  const handleClose = () => {
    setName('');
    setDescription('');
    setRegionId('');
    setPrecinctId('');
    setProcedureId('');
    setSelectedClusters([]);
    setValidationErrors({});
    setApiError('');
    onClose();
  };

  // Handle Name Change
  const handleNameChange = (e) => {
    const value = e.target.value;
    setName(value);
    if (validationErrors.name && value.trim()) {
      setValidationErrors((prev) => ({
        ...prev,
        name: '',
      }));
    }
  };

  // Handle Description Change
  const handleDescriptionChange = (e) => {
    const value = e.target.value;
    setDescription(value);
    if (validationErrors.description && value.trim()) {
      setValidationErrors((prev) => ({
        ...prev,
        description: '',
      }));
    }
  };

  // Handle Region Change
  const handleRegionChange = (e) => {
    const value = e.target.value;
    setRegionId(value);
    setPrecinctId(''); // Reset precinct when region changes
    if (validationErrors.regionId && value) {
      setValidationErrors((prev) => ({
        ...prev,
        regionId: '',
      }));
    }
  };

  // Handle Precinct Change
  const handlePrecinctChange = (e) => {
    const value = e.target.value;
    setPrecinctId(value);
    if (validationErrors.precinctId && value) {
      setValidationErrors((prev) => ({
        ...prev,
        precinctId: '',
      }));
    }
  };

  // Handle Procedure Change
  const handleProcedureChange = (e) => {
    const value = e.target.value;
    setProcedureId(value);
    if (validationErrors.procedureId && value) {
      setValidationErrors((prev) => ({
        ...prev,
        procedureId: '',
      }));
    }
  };

  // Handle Cluster Selection
  // Now works with both create (string IDs) and edit (object or string IDs)
  const handleClusterChange = (e) => {
    const clusterId = e.target.value;
    const isChecked = e.target.checked;

    let updatedClusters;
    if (isChecked) {
      updatedClusters = [...selectedClusters, clusterId];
    } else {
      updatedClusters = selectedClusters.filter((id) => id !== clusterId);
    }

    setSelectedClusters(updatedClusters);

    if (validationErrors.clusterArray && updatedClusters.length > 0) {
      setValidationErrors((prev) => ({
        ...prev,
        clusterArray: '',
      }));
    }
  };

  // Check if a cluster is selected (handles both string IDs and object IDs)
  const isClusterSelected = (clusterId) => {
    return selectedClusters.some((id) => {
      // Compare as strings to handle any format mismatch
      return String(id) === String(clusterId);
    });
  };

  // Footer
  const footer = (
    <ButtonContainer>
      <PrimaryButton
        type="submit"
        form="cluster-flow-form"
        disabled={
          isLoading ||
          !name.trim() ||
          !description.trim() ||
          (!isEdit && (!regionId || !precinctId || !procedureId)) ||
          selectedClusters.length === 0 ||
          Object.keys(validationErrors).length > 0
        }
      >
        {isLoading ? (isEdit ? 'Updating…' : 'Creating…') : isEdit ? 'Update Flow' : 'Create Flow'}
      </PrimaryButton>
    </ButtonContainer>
  );

  return (
    <AdminModal
      isOpen={true}
      onClose={handleClose}
      title={isEdit ? 'Edit Cluster Flow' : 'Add Cluster Flow'}
      footer={footer}
      width="600px"
    >
      <FormWrapper id="cluster-flow-form" onSubmit={handleSubmit}>
        {/* API Error */}
        {apiError && (
          <div
            style={{
              backgroundColor: '#ffebee',
              color: '#c62828',
              padding: '12px',
              borderRadius: '4px',
              marginBottom: '16px',
              fontSize: '14px',
              border: '1px solid #ef5350',
            }}
          >
            {apiError}
          </div>
        )}

        {/* Name */}
        <FormSection>
          <FieldGroup>
            <Label htmlFor="name">Name *</Label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={handleNameChange}
              placeholder="Enter flow name..."
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: validationErrors.name ? '1px solid #d32f2f' : '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
                outline: 'none',
                boxSizing: 'border-box',
                transition: 'border-color 0.2s ease',
                backgroundColor: isLoading ? '#f5f5f5' : '#fff',
                cursor: isLoading ? 'not-allowed' : 'text',
              }}
            />
            {validationErrors.name && <ErrorText>{validationErrors.name}</ErrorText>}
          </FieldGroup>
        </FormSection>

        {/* Description */}
        <FormSection>
          <FieldGroup>
            <Label htmlFor="description">Description *</Label>
            <textarea
              id="description"
              value={description}
              onChange={handleDescriptionChange}
              placeholder="Enter flow description..."
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: validationErrors.description ? '1px solid #d32f2f' : '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
                outline: 'none',
                boxSizing: 'border-box',
                fontFamily: 'inherit',
                minHeight: '80px',
                resize: 'vertical',
                transition: 'border-color 0.2s ease',
                backgroundColor: isLoading ? '#f5f5f5' : '#fff',
                cursor: isLoading ? 'not-allowed' : 'text',
              }}
            />
            {validationErrors.description && <ErrorText>{validationErrors.description}</ErrorText>}
          </FieldGroup>
        </FormSection>

        {/* Region Dropdown - Only in Create Mode */}
        {!isEdit && (
          <FormSection>
            <FieldGroup>
              <Label htmlFor="region">Select Region *</Label>
              <select
                id="region"
                value={regionId}
                onChange={handleRegionChange}
                disabled={isLoading || zonesLoading}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: validationErrors.regionId ? '1px solid #d32f2f' : '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.2s ease',
                  backgroundColor: isLoading || zonesLoading ? '#f5f5f5' : '#fff',
                  cursor: isLoading || zonesLoading ? 'not-allowed' : 'pointer',
                }}
              >
                <option value="">-- Select Region --</option>
                {regionsData.map((region) => (
                  <option key={region._id} value={region._id}>
                    {region?.regionName}
                  </option>
                ))}
              </select>
              {validationErrors.regionId && <ErrorText>{validationErrors.regionId}</ErrorText>}
            </FieldGroup>
          </FormSection>
        )}

        {/* Precinct Dropdown - Only in Create Mode */}
        {!isEdit && (
          <FormSection>
            <FieldGroup>
              <Label htmlFor="precinct">Select Precinct *</Label>
              <select
                id="precinct"
                value={precinctId}
                onChange={handlePrecinctChange}
                disabled={isLoading || precinctLoading || !regionId}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: validationErrors.precinctId ? '1px solid #d32f2f' : '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.2s ease',
                  backgroundColor: isLoading || precinctLoading || !regionId ? '#f5f5f5' : '#fff',
                  cursor: isLoading || precinctLoading || !regionId ? 'not-allowed' : 'pointer',
                }}
              >
                <option value="">-- Select Precinct --</option>
                {precinctsList.map((precinct) => (
                  <option key={precinct._id} value={precinct._id}>
                    {precinct.precinctName}
                  </option>
                ))}
              </select>
              {validationErrors.precinctId && <ErrorText>{validationErrors.precinctId}</ErrorText>}
              {!regionId && (
                <ErrorText style={{ color: '#666', fontSize: '12px' }}>
                  Please select a region first
                </ErrorText>
              )}
            </FieldGroup>
          </FormSection>
        )}

        {/* Development Type (Procedure) Dropdown - Only in Create Mode */}
        {!isEdit && (
          <FormSection>
            <FieldGroup>
              <Label htmlFor="procedure">Development Type *</Label>
              <select
                id="procedure"
                value={procedureId}
                onChange={handleProcedureChange}
                disabled={isLoading || proceduresLoading}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: validationErrors.procedureId ? '1px solid #d32f2f' : '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.2s ease',
                  backgroundColor: isLoading || proceduresLoading ? '#f5f5f5' : '#fff',
                  cursor: isLoading || proceduresLoading ? 'not-allowed' : 'pointer',
                }}
              >
                <option value="">-- Select Development Type --</option>
                {proceduresData.map((procedure) => (
                  <option key={procedure._id} value={procedure._id}>
                    {procedure.procedureName}
                  </option>
                ))}
              </select>
              {validationErrors.procedureId && (
                <ErrorText>{validationErrors.procedureId}</ErrorText>
              )}
            </FieldGroup>
          </FormSection>
        )}

        {/* Add Clusters - Multi-Select Checkboxes */}
        <FormSection>
          <FieldGroup>
            <Label>Add Clusters *</Label>
            <div
              style={{
                border: validationErrors.clusterArray ? '1px solid #d32f2f' : '1px solid #ccc',
                borderRadius: '4px',
                padding: '12px',
                maxHeight: '200px',
                overflowY: 'auto',
                backgroundColor: isLoading ? '#f5f5f5' : '#fff',
              }}
            >
              {clustersData.length === 0 ? (
                <p style={{ margin: 0, color: '#999', fontSize: '14px' }}>No clusters available</p>
              ) : (
                clustersData.map((cluster) => (
                  <div
                    key={cluster._id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '8px 0',
                      borderBottom: '1px solid #f0f0f0',
                    }}
                  >
                    <input
                      type="checkbox"
                      id={`cluster-${cluster._id}`}
                      value={cluster._id}
                      checked={isClusterSelected(cluster._id)}
                      onChange={handleClusterChange}
                      disabled={isLoading || clustersLoading}
                      style={{
                        marginRight: '8px',
                        cursor: isLoading ? 'not-allowed' : 'pointer',
                      }}
                    />
                    <label
                      htmlFor={`cluster-${cluster._id}`}
                      style={{
                        cursor: isLoading ? 'not-allowed' : 'pointer',
                        margin: 0,
                        fontSize: '14px',
                        flex: 1,
                      }}
                    >
                      {cluster.name}
                    </label>
                  </div>
                ))
              )}
            </div>
            {validationErrors.clusterArray && (
              <ErrorText>{validationErrors.clusterArray}</ErrorText>
            )}
            <p
              style={{
                fontSize: '12px',
                color: '#666',
                margin: '8px 0 0 0',
              }}
            >
              Selected: {selectedClusters.length} cluster(s)
            </p>
          </FieldGroup>
        </FormSection>
      </FormWrapper>
    </AdminModal>
  );
};

export default ClusterFlowCreateAndUpdateModal;
